import { boolFromUnknown, intFromUnknown, strFromUnknown } from '@/lib/primitive-utils';
import { LabeledCheckboxRow, LabeledSelectRow, LabeledTextRow } from './shared/field-primitives';
import { AcceptENotationCheckbox, AcceptScientificNotationCheckbox, HideENotationCheckbox } from './shared/scientific-notation-fields';
import { AbsoluteValueCheckbox, ToleranceField, WidthField } from './shared/tolerance-width-fields';
import type { BuilderPropertyRecord, ShortAnswerToolSettingsProps } from './tool-defaults';

const UNITLESS_OPTIONS = ['Not unitless', 'Unitless length', 'Unitless area'];
const NOTATION_UI = ['Engineering', 'Strict scientific', 'None'];
type NotationUi = (typeof NOTATION_UI)[number];

function notationUiFromProps(p: BuilderPropertyRecord): NotationUi {
    const eng = boolFromUnknown(p.require_engineering_notation);
    const strict = boolFromUnknown(p.require_strict_scientific_notation);
    if (eng && !strict) {
        return 'Engineering';
    }
    if (!eng && strict) {
        return 'Strict scientific';
    }
    return 'None';
}

function patchForNotationUi(ui: NotationUi): BuilderPropertyRecord {
    switch (ui) {
        case 'Engineering':
            return {
                accept_E_notation: false,
                accept_scientific_notation: false,
                hide_E_notation: false,
                require_engineering_notation: true,
                require_strict_scientific_notation: false,
            };
        case 'Strict scientific':
            return {
                accept_E_notation: false,
                hide_E_notation: false,
                require_engineering_notation: false,
                require_strict_scientific_notation: true,
            };
        case 'None':
            return {
                require_engineering_notation: false,
                require_strict_scientific_notation: false,
            };
        default:
            return {};
    }
}

export function selectNumberAndUnitsToolProps(p: BuilderPropertyRecord) {
    const notationUi = notationUiFromProps(p);
    return {
        notationUi,
        showNotationDetail: notationUi === 'None',
        acceptScientificNotation: boolFromUnknown(p.accept_scientific_notation),
        acceptENotation: boolFromUnknown(p.accept_E_notation),
        hideENotation: boolFromUnknown(p.hide_E_notation),
        unit_prefixes: strFromUnknown(p.unit_prefixes, ''),
        physical_quantities: strFromUnknown(p.physical_quantities, ''),
        unitless_option: strFromUnknown(p.unitless_option, 'Not unitless'),
        preferred_units: strFromUnknown(p.preferred_units, ''),
        tolerance: strFromUnknown(p.tolerance, '3%'),
        absoluteValue: boolFromUnknown(p.absolute_value),
        give_units: boolFromUnknown(p.give_units),
        width: intFromUnknown(p.width, 150),
    };
}

function NumberAndUnitsToolSettingsInner(
    props: ReturnType<typeof selectNumberAndUnitsToolProps> & {
        onPatch: (patch: BuilderPropertyRecord) => void;
    },
) {
    const {
        notationUi,
        showNotationDetail,
        acceptScientificNotation,
        acceptENotation,
        hideENotation,
        unit_prefixes,
        physical_quantities,
        unitless_option,
        preferred_units,
        tolerance,
        absoluteValue,
        give_units,
        width,
        onPatch,
    } = props;

    return (
        <div className="space-y-3">
            <LabeledSelectRow
                label="Required notation"
                value={notationUi}
                options={NOTATION_UI}
                onValueChange={v => onPatch(patchForNotationUi(v as NotationUi))}
            />
            {showNotationDetail ? (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-2 sm:gap-x-3 sm:gap-y-2">
                    <AcceptScientificNotationCheckbox
                        value={acceptScientificNotation}
                        onPatch={onPatch}
                    />
                    <AcceptENotationCheckbox
                        value={acceptENotation}
                        onPatch={onPatch}
                    />
                    {acceptENotation && (
                        <HideENotationCheckbox
                            value={hideENotation}
                            onPatch={onPatch}
                        />
                    )}
                </div>
            ) : null}

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-2 sm:gap-x-3 sm:gap-y-2">
                <LabeledTextRow
                    label="Unit prefixes (Special: μ)"
                    value={unit_prefixes}
                    rows={2}
                    onValueChange={v => onPatch({ unit_prefixes: v })}
                />
                <LabeledTextRow
                    label="Physical quantities (Special: Ω and °C)"
                    value={physical_quantities}
                    rows={2}
                    onValueChange={v => onPatch({ physical_quantities: v })}
                />
                <LabeledSelectRow
                    label="Unitless?"
                    value={unitless_option}
                    options={UNITLESS_OPTIONS}
                    onValueChange={v => onPatch({ unitless_option: v })}
                />
                <LabeledTextRow
                    label="Optional: Preferred units (Ex: \QN,\Qm,\QA)"
                    value={preferred_units}
                    rows={1}
                    onValueChange={v => onPatch({ preferred_units: v })}
                />
                <ToleranceField
                    tolerance={tolerance}
                    onPatch={onPatch}
                />
                <WidthField
                    width={width}
                    onPatch={onPatch}
                />
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-2 sm:gap-x-3 sm:gap-y-2">
                <AbsoluteValueCheckbox
                    absoluteValue={absoluteValue}
                    onPatch={onPatch}
                />
                <LabeledCheckboxRow
                    label="Give units"
                    checked={give_units}
                    onCheckedChange={v => onPatch({ give_units: v })}
                />
            </div>
        </div>
    );
}

export function NumberAndUnitsToolSettings({ builderProps, onPatch }: ShortAnswerToolSettingsProps) {
    return (
        <NumberAndUnitsToolSettingsInner
            {...selectNumberAndUnitsToolProps(builderProps)}
            onPatch={onPatch}
        />
    );
}
